import { EventProperties } from '@calm/evexia-analytics';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { PollRecordedQuestion } from '@calm/evexia-web-sdk';
import { SuccessCheckAnimation } from '@rhg/components/animations/success-check-animation';
import { PollQuestion } from '@rhg/components/poll/question';
import useConfirmation from '@rhg/hooks/use-confirmation';
import { usePersonalizingCarePlan } from '@rhg/hooks/use-personalizing-care-plan';
import { usePoll } from '@rhg/hooks/use-poll';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import { useSavePoll } from '@rhg/hooks/use-save-poll';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { Grid, Stack, Typography } from '@rhg/libs/material';
import { WarningOutline } from '@rhg/libs/material/icons';
import SimpleDialog from '@rhg/libs/material/simple-dialog';
import { SimpleDialogTitle } from '@rhg/libs/material/simple-dialog/title';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useCallback, useId, useState } from 'react';
import { useIntl } from 'react-intl';

const styleTitle = { mt: 2, mb: DS_SPACING_UNITS.SECTION };

interface AllGuidesCompletedProps {
    npsPollId: string;
    onClose: () => void;
    isOpen: boolean;
    showUpdatePlan?: boolean;
}
export function AllGuidesCompleted({
    npsPollId,
    onClose,
    isOpen,
    showUpdatePlan = true,
}: AllGuidesCompletedProps) {
    const id = useId();
    const { formatMessage } = useIntl();
    const { program, workflow } = useProvidedProgramContext();
    const { push } = useRouter();
    const { openPersonalizingCarePlan } = usePersonalizingCarePlan();
    const { poll, isFetched } = usePoll(npsPollId);
    const { savePoll } = useSavePoll();
    const { confirm } = useConfirmation();
    const [answer, setAnswer] = useState<PollRecordedQuestion>();

    const submitPoll = useCallback(
        (willSkip = false) => {
            if (!willSkip && !answer) {
                confirm({
                    title: formatMessage(
                        programMessages['program.please_select_rating']
                    ),
                    SimpleDialogProps: { titleVariant: 'h6' },
                    noCancelButton: true,
                    trackDialog: 'PostSessionProgramCompleted',
                    confirmLabel: formatMessage(commonMessages['common.ok']),
                    fullScreen: false,
                    bodyTextAlign: 'center',
                    icon: <WarningOutline color="inherit" />,
                    iconColor: 'warning',
                });
            } else {
                savePoll(
                    {
                        pollId: npsPollId,
                        workflowDefnId: workflow?.workflowDefn.defnId,
                        workflowType: workflow?.workflowDefn.type,
                        request: willSkip
                            ? { skipped: true }
                            : { questions: [answer!] },
                    },
                    {
                        onSuccess: () => {
                            if (showUpdatePlan) {
                                openPersonalizingCarePlan(
                                    willSkip
                                        ? {}
                                        : {
                                              subtitle: formatMessage(
                                                  programMessages[
                                                      'program.rating_successful'
                                                  ]
                                              ),
                                          }
                                );
                                push(routes.careplan);
                            }
                            onClose();
                        },
                    }
                );
            }
        },
        [
            answer,
            confirm,
            formatMessage,
            savePoll,
            npsPollId,
            workflow?.workflowDefn.defnId,
            workflow?.workflowDefn.type,
            openPersonalizingCarePlan,
            push,
            onClose,
            showUpdatePlan,
        ]
    );

    const trackProps: EventProperties = {
        ProgramId: program?.id ?? '',
        ProgramName: program?.title ?? '',
        NpsScore: answer?.answer?.range?.toString() ?? 'null',
    };

    return (
        <SimpleDialog
            onClose={() => submitPoll(true)}
            isOpen={isOpen}
            trackDialog="NpsPoll"
            trackScreen="NpsPoll"
            trackFeature="ClinicalProgram"
            fullScreen
            icon={<SuccessCheckAnimation />}
            noIconPadding
            iconColor="info"
            aria-labelledby={id}
            hideCloseButton
            buttons={[
                {
                    trackButton: 'NpsSkipButton',
                    trackProps,
                    title: formatMessage(commonMessages['common.skip']),
                    onClick: () => submitPoll(true),
                    variant: 'outlined',
                },
                {
                    trackButton: 'NpsRateButton',
                    trackProps,
                    title: formatMessage(
                        programMessages['program.rate_program']
                    ),
                    onClick: () => submitPoll(),
                },
            ]}
            hasGradientBackground={true}
            content={
                <Stack>
                    <Stack alignItems="center" mb={DS_SPACING_UNITS.CONTENT}>
                        <Typography
                            variant="subtitle1"
                            color="text.sub"
                            component="p"
                        >
                            {formatMessage(
                                programMessages['program.youve_completed']
                            )}
                        </Typography>
                        <SimpleDialogTitle
                            title={program?.title}
                            titleId={id}
                            sx={styleTitle}
                        />
                        {showUpdatePlan ? (
                            <Typography
                                variant="body1"
                                color="text.body"
                                textAlign="center"
                            >
                                {formatMessage(
                                    programMessages[
                                        'program.update_care_plan_after_rating'
                                    ]
                                )}
                            </Typography>
                        ) : (
                            <Typography
                                variant="body1"
                                color="text.body"
                                textAlign="center"
                            >
                                {formatMessage(
                                    programMessages[
                                        'program.program_completed.add_rating'
                                    ]
                                )}
                            </Typography>
                        )}
                    </Stack>
                    <Grid container>
                        <Grid item xs={12}>
                            {isFetched && poll?.questions?.[0] && (
                                <PollQuestion
                                    showDescription={false}
                                    question={poll?.questions?.[0]}
                                    onAnswer={setAnswer}
                                    variant="nps"
                                />
                            )}
                        </Grid>
                    </Grid>
                </Stack>
            }
        />
    );
}
